import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useApp, API } from "../store";
import { X, ArrowRight } from "lucide-react";

export default function ProductCompare({ currentProduct }) {
  const { locale } = useApp();
  const [siblings, setSiblings] = useState([]);
  const [selected, setSelected] = useState([]);

  useEffect(() => {
    if (!currentProduct?.category) return;
    axios
      .get(`${API}/products?category=${currentProduct.category}`)
      .then((r) => {
        const others = (r.data || []).filter((p) => p.id !== currentProduct.id && !p.is_accessory);
        setSiblings(others);
      })
      .catch(() => {});
  }, [currentProduct]);

  const toggle = (slug) => {
    setSelected((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : prev.length < 3 ? [...prev, slug] : prev
    );
  };

  const compareItems = siblings.filter((p) => selected.includes(p.slug));
  const allSpecs = Array.from(
    new Set(compareItems.flatMap((p) => Object.keys(p.specs || {})))
  );

  if (siblings.length === 0) return null;

  return (
    <section className="mt-16 border-t border-slate-200 pt-10" data-testid="product-compare">
      <div className="overline mb-2 text-slate-500">
        {locale === "pt" ? "Compare modelos" : locale === "es" ? "Comparar modelos" : "Compare models"}
      </div>
      <h2 className="font-serif-display text-3xl md:text-4xl text-slate-900 mb-6">
        {locale === "pt" ? "Compare com outros modelos" : locale === "es" ? "Compara con otros modelos" : "Compare with other models"}
      </h2>

      {/* Product selection chips */}
      <div className="flex flex-wrap gap-3 mb-8">
        {siblings.map((p) => {
          const active = selected.includes(p.slug);
          return (
            <button
              key={p.id}
              onClick={() => toggle(p.slug)}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl border-2 transition-all text-left ${
                active
                  ? "border-sky-500 bg-sky-50"
                  : "border-slate-200 hover:border-slate-400 bg-white"
              }`}
            >
              {p.images?.[0] && (
                <img src={p.images[0]} alt={p.name} className="w-10 h-10 object-cover rounded-lg flex-shrink-0" />
              )}
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{p.name}</div>
                <div className="text-xs text-slate-500">{p.tagline}</div>
              </div>
              {active && <X size={14} className="text-sky-500 flex-shrink-0" />}
            </button>
          );
        })}
      </div>

      {/* Comparison table */}
      {compareItems.length > 0 && (
        <div className="overflow-x-auto">
          <div
            className="grid gap-px bg-slate-200 border border-slate-200 rounded-lg overflow-hidden"
            style={{
              gridTemplateColumns: `140px repeat(${compareItems.length}, minmax(160px, 1fr))`,
            }}
          >
            {/* Header row */}
            <div className="bg-slate-50 p-4 text-xs uppercase tracking-widest text-slate-500 font-semibold">
              &nbsp;
            </div>
            {compareItems.map((p) => (
              <div key={p.id} className="bg-white p-4 text-center">
                {p.images?.[0] && (
                  <img src={p.images[0]} alt={p.name} className="w-full aspect-[4/5] object-cover rounded-lg mb-3" />
                )}
                <div className="font-serif-display text-lg text-slate-900">{p.name}</div>
                <div className="text-sky-600 text-sm mt-1">${p.base_price?.toLocaleString()}</div>
                <Link
                  to={`/product/${p.slug}`}
                  className="inline-flex items-center gap-1 text-xs text-sky-500 hover:text-sky-700 mt-2"
                >
                  <ArrowRight size={12} />
                  {locale === "pt" ? "Ver detalhes" : locale === "es" ? "Ver detalles" : "View details"}
                </Link>
              </div>
            ))}

            {/* Spec rows */}
            {allSpecs.map((key) => (
              <React.Fragment key={key}>
                <div className="bg-slate-50 p-3 text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center">
                  {key.replace(/_/g, " ")}
                </div>
                {compareItems.map((p) => (
                  <div key={p.id} className="bg-white p-3 text-center text-sm text-slate-900">
                    {p.specs?.[key] ?? "—"}
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {selected.length === 0 && (
        <p className="text-slate-400 text-sm mt-4">
          {locale === "pt"
            ? "Selecione modelos acima para comparar especificações lado a lado."
            : locale === "es"
            ? "Selecciona modelos arriba para comparar especificaciones lado a lado."
            : "Select models above to compare specifications side by side."}
        </p>
      )}
    </section>
  );
}
